/**
 * lazyRetry — Wraps React.lazy with automatic retry on chunk load failure.
 * Prevents blank screens when a deployment invalidates cached JS chunks.
 * Retries up to 3 times with exponential backoff before giving up.
 */
import { lazy, type ComponentType } from "react";

const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 1000;

export function lazyRetry<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
): React.LazyExoticComponent<T> {
  return lazy(() => retryImport(factory));
}

async function retryImport<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
  attempt = 0,
): Promise<{ default: T }> {
  try {
    return await factory();
  } catch (error) {
    if (attempt >= MAX_RETRIES) {
      throw error;
    }

    // Wait with exponential backoff
    await new Promise((r) => setTimeout(r, RETRY_DELAY_MS * 2 ** attempt));

    // Bust cache by adding timestamp param for module URLs
    // The browser may have cached the failed import
    return retryImport(factory, attempt + 1);
  }
}
